import { useCallback, useEffect, useRef, useState } from "react";
import { m, AnimatePresence } from "framer-motion";
import Portal from "./Portal";
import { usePopupManager } from "./PopupManager";
import { trackOutboundLink, trackEvent } from "@/lib/analytics";
import { JORGE } from "@/constants";

interface KickPromoPopupProps {
  /** Delay in ms before the popup appears (default: 210 000 = 3.5 min). */
  delayMs?: number;
  /** How long the popup stays visible in ms (default: 20 000 = 20 s). */
  durationMs?: number;
  language?: "en" | "es";
}

const POPUP_ID = "kick-promo";
const KICK_URL = JORGE.SOCIAL.KICK;
const DISMISSED_KEY = "mb_kick_promo_dismissed";

// Only show once per session by default; respect a 24-hour localStorage cooldown.
function shouldShow(): boolean {
  if (typeof window === "undefined") return false;
  const dismissed = localStorage.getItem(DISMISSED_KEY);
  if (!dismissed) return true;
  const ts = Number(dismissed);
  if (Number.isNaN(ts)) return true;
  return Date.now() - ts > 24 * 60 * 60 * 1000;
}

function markDismissed() {
  try {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
  } catch {
    // Storage full or blocked — silently ignore.
  }
}

const content = {
  en: {
    heading: "Watch us grow real businesses — live",
    body: "See how we build websites, run SEO campaigns, and help local businesses get more customers. Live on Kick every week.",
    cta: "Follow on Kick",
    dismiss: "Maybe later",
  },
  es: {
    heading: "Mira cómo hacemos crecer negocios — en vivo",
    body: "Mira cómo construimos sitios web, ejecutamos campañas SEO y ayudamos a negocios locales a conseguir más clientes. En vivo en Kick cada semana.",
    cta: "Seguir en Kick",
    dismiss: "Quizás después",
  },
};

const KickPromoPopup: React.FC<KickPromoPopupProps> = ({
  delayMs = 210_000,
  durationMs = 20_000,
  language = "en",
}) => {
  const { request, dismiss, activeId } = usePopupManager();
  const [visible, setVisible] = useState(false);
  const autoHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  // Once the popup is closed in this session, it must never reopen — even
  // if activeId briefly reads stale during the dismiss cycle.
  const closedRef = useRef(false);
  const t = content[language];

  // Clear auto-hide timer on unmount or when dismissed.
  const clearAutoHide = useCallback(() => {
    if (autoHideTimer.current) {
      clearTimeout(autoHideTimer.current);
      autoHideTimer.current = null;
    }
  }, []);

  // Tear down without recording anything — used when the visitor ACCEPTED the CTA,
  // where a popup_dismiss event would be a lie.
  const closeSilently = useCallback(() => {
    if (closedRef.current) return;
    closedRef.current = true;
    clearAutoHide();
    setVisible(false);
    markDismissed();
    dismiss(POPUP_ID);
  }, [clearAutoHide, dismiss]);

  const close = useCallback(() => {
    if (closedRef.current) return;
    closedRef.current = true;
    clearAutoHide();
    setVisible(false);
    markDismissed();
    dismiss(POPUP_ID);
    trackEvent("popup_dismiss", { popup_id: POPUP_ID });
  }, [clearAutoHide, dismiss]);

  // Register the popup in the manager queue after delay.
  useEffect(() => {
    if (!shouldShow()) return;

    const delay = setTimeout(() => {
      const cleanup = request({
        id: POPUP_ID,
        priority: "low",
        render: () => null, // Render is handled inside this component via `visible`.
      });
      cleanupRef.current = cleanup;
    }, delayMs);

    return () => {
      clearTimeout(delay);
      cleanupRef.current?.();
      clearAutoHide();
    };
  }, [delayMs, request, clearAutoHide]);

  // When PopupManager gives us the slot, show the popup.
  useEffect(() => {
    if (closedRef.current) return;
    if (activeId === POPUP_ID && !visible) {
      setVisible(true);
      trackEvent("popup_show", { popup_id: POPUP_ID });

      // Auto-hide after duration.
      autoHideTimer.current = setTimeout(close, durationMs);
    }
  }, [activeId, visible, durationMs, close]);

  // Escape key handler.
  useEffect(() => {
    if (!visible) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [visible, close]);

  // Accepting the CTA previously fired THREE events: cta_click, outbound_click, and —
  // because close() tracks — popup_dismiss, which recorded taking the offer as declining
  // it. Now: one outbound_click, and the popup closes without a dismissal event.
  const handleKickClick = () => {
    trackOutboundLink(KICK_URL);
    closeSilently();
  };

  return (
    <Portal id="kick-promo-portal">
      <AnimatePresence>
        {visible && (
          <m.div
            key="kick-promo-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[9998] bg-black/30 backdrop-blur-sm"
            onClick={close}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {visible && (
          <m.div
            key="kick-promo-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="kick-promo-heading"
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ type: "spring", damping: 24, stiffness: 300 }}
            className="fixed z-[9999] bottom-6 right-6 w-[min(380px,calc(100vw-2rem))] rounded-2xl bg-dark-800 border border-dark-600 shadow-2xl shadow-black/40 overflow-hidden"
          >
            {/* Top accent */}
            <div className="h-1 bg-gradient-to-r from-[#53FC18] via-[#53FC18]/60 to-transparent" />

            <div className="p-5">
              {/* Header row */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-[#53FC18]/10 text-[#53FC18]">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M1.333 0h8v5.333H12V2.667h2.667V0h8v8H20v2.667h-2.667v2.666H20V16h2.667v8h-8v-2.667H12v-2.666H9.333V24h-8Z" />
                    </svg>
                  </div>
                  <div>
                    <h3 id="kick-promo-heading" className="text-sm font-bold text-dark-50">
                      {t.heading}
                    </h3>
                    <span className="text-[11px] font-mono text-[#53FC18]/80">
                      kick.com/marketbrewer
                    </span>
                  </div>
                </div>

                {/* Close button */}
                <button
                  type="button"
                  onClick={close}
                  className="p-1 -mr-1 -mt-1 rounded-md text-dark-500 hover:text-dark-200 hover:bg-dark-700 transition-colors focus:outline-none focus:ring-2 focus:ring-metro-orange-500"
                  aria-label="Close"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <p className="text-dark-400 text-sm leading-relaxed mb-4">
                {t.body}
              </p>

              {/* Actions */}
              <div className="flex items-center gap-3">
                <a
                  href={KICK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleKickClick}
                  data-mb-tracked="1"
                  className="inline-flex items-center gap-2 flex-1 justify-center px-4 py-2.5 text-sm font-semibold rounded-lg text-dark-950 bg-[#53FC18] hover:bg-[#47e015] active:bg-[#3cc712] transition-colors focus:outline-none focus:ring-2 focus:ring-[#53FC18] focus:ring-offset-2 focus:ring-offset-dark-800"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M1.333 0h8v5.333H12V2.667h2.667V0h8v8H20v2.667h-2.667v2.666H20V16h2.667v8h-8v-2.667H12v-2.666H9.333V24h-8Z" />
                  </svg>
                  {t.cta}
                </a>
                <button
                  type="button"
                  onClick={close}
                  className="px-4 py-2.5 text-sm font-medium text-dark-400 hover:text-dark-200 rounded-lg hover:bg-dark-700 transition-colors focus:outline-none focus:ring-2 focus:ring-metro-orange-500"
                >
                  {t.dismiss}
                </button>
              </div>
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </Portal>
  );
};

export default KickPromoPopup;
